import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './styles.css';
import hazardIcon from '../../images/hazardIcon.svg';
import { useModalFocusReturn } from '../../hooks/useModalFocusReturn';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';

const DistancePanel = ({ isOpen, onClose, currentDistance = '25', onDistanceSelect }) => {
    const [selectedDistance, setSelectedDistance] = useState(currentDistance);
    const [customDistance, setCustomDistance] = useState('');
    const [distanceError, setDistanceError] = useState('');
    const panelRef = React.useRef(null);
    const closeButtonRef = React.useRef(null);

    // Restore focus to whatever triggered this panel once it closes.
    useModalFocusReturn(isOpen);

    // Lock page scroll while open, without jumping to the top and correctly restoring
    // the exact scroll position on close.
    useBodyScrollLock(isOpen);

    // Move focus INTO the panel (to its close button) as soon as it opens, instead of
    // leaving focus on whatever link/button was just clicked to open it. Without this,
    // keyboard users have to Tab all the way through the rest of the page to reach the
    // now-open modal — this is declared AFTER useModalFocusReturn so that hook's
    // "capture the trigger element" effect (which runs in declaration order) sees the
    // real outside trigger, not this close button.
    useEffect(() => {
        if (isOpen) {
            closeButtonRef.current?.focus();
        }
    }, [isOpen]);

    useEffect(() => {
        setSelectedDistance(currentDistance);
    }, [currentDistance, isOpen]);

    useEffect(() => {
        if (isOpen && panelRef.current) {
            // Query focusable elements FRESH on every Tab press rather than capturing a
            // single stale NodeList when the panel opens. The previous version captured
            // `firstElement`/`lastElement` once — that broke as soon as the "Apply
            // distance" button's `disabled` attribute changed after the panel opened
            // (e.g. focusing the custom-distance input sets selectedDistance to 'custom',
            // which disables Apply until a value is typed). A native `disabled` button
            // can never become `document.activeElement`, so the captured `lastElement`
            // reference stopped matching anything — Tab from the custom input then fell
            // through to the (still in-DOM, only visually hidden behind the overlay)
            // background page instead of wrapping back to the panel's first element,
            // exactly matching the reported "focus goes back to Find care page" bug.
            // Recomputing on every keydown also means a genuinely disabled/removed
            // element is correctly excluded from `:not([disabled])` at press-time,
            // instead of trusting a snapshot that can go stale.
            const getFocusable = () => Array.from(
                panelRef.current.querySelectorAll(
                    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
                )
            );

            const handleTabKey = (e) => {
                if (e.key !== 'Tab') return;
                const focusableElements = getFocusable();
                if (focusableElements.length === 0) return;
                const firstElement = focusableElements[0];
                const lastElement = focusableElements[focusableElements.length - 1];

                if (e.shiftKey && document.activeElement === firstElement) {
                    e.preventDefault();
                    lastElement?.focus();
                } else if (!e.shiftKey && document.activeElement === lastElement) {
                    e.preventDefault();
                    firstElement?.focus();
                }
            };

            panelRef.current.addEventListener('keydown', handleTabKey);
            return () => {
                panelRef.current?.removeEventListener('keydown', handleTabKey);
            };
        }
    }, [isOpen]);

    const handleOverlayClick = (e) => {
        if (e.target.classList.contains('distance-panel-overlay')) {
            onClose();
        }
    };

    const handleDistanceChange = (distance) => {
        setSelectedDistance(distance);
        // Clear custom distance if selecting a preset
        if (distance !== 'custom') {
            setCustomDistance('');
            setDistanceError('');
        }
    };

    const handleCustomDistanceChange = (e) => {
        const value = e.target.value;
        if (value === '' || /^\d+$/.test(value)) {
            setCustomDistance(value);
            setSelectedDistance('custom');
            
            // Check if value is over 250
            if (value && Number(value) > 250) {
                setDistanceError('Be sure to type a number up to 250 miles.');
            } else {
                setDistanceError('');
            }
        }
    };

    const handleApply = () => {
        let distanceToApply = selectedDistance;
        
        // If custom is selected, use the custom distance value
        if (selectedDistance === 'custom' && customDistance) {
            // Don't allow values over 250
            if (Number(customDistance) > 250) {
                return;
            }
            distanceToApply = customDistance;
        }
        
        // Don't apply if custom is selected but no value entered
        if (selectedDistance === 'custom' && !customDistance) {
            return;
        }

        onDistanceSelect(distanceToApply);
        onClose();
    };

    const handleClose = () => {
        onClose();
    };

    if (!isOpen) return null;

    const isApplyDisabled = Boolean((selectedDistance === 'custom' && !customDistance) || distanceError);

    const distanceOptions = [
        { value: '5', label: 'Within 5 miles' },
        { value: '25', label: 'Within 25 miles' },
        { value: '50', label: 'Within 50 miles' },
        { value: '100', label: 'Within 100 miles' },
        { value: '150', label: 'Within 150 miles' },
        { value: '200', label: 'Within 200 miles' }
    ];

    // Rendered via a portal directly under <body> so that this panel's
    // `position: fixed` overlay is always positioned relative to the
    // viewport - not to whatever ancestor happens to render <DistancePanel>.
    // Without this, an ancestor with a CSS `transform` (e.g. `.results-page`'s
    // translateZ(0) compositor-layer fix) becomes the containing block for
    // fixed-position descendants, causing this panel to be mis-sized/offset.
    return createPortal(
        <div 
            className="distance-panel-overlay"
            onClick={handleOverlayClick}
            onKeyDown={(e) => { if (e.key === 'Escape') handleClose(); }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="distance-panel-title"
        >
            <div className="distance-side-panel" ref={panelRef}>
                <div className="distance-panel-header">
                    <h3 id="distance-panel-title" className="distance-panel-title">
                        Distance
                    </h3>
                    <button
                        className="distance-close-button"
                        onClick={handleClose}
                        aria-label="Close distance panel"
                        type="button"
                        ref={closeButtonRef}
                    >
                        ×
                    </button>
                </div>

                <div className="distance-panel-content">
                    <p className="distance-description">
                        Choose what distance you'd like to search within. Or type in your own distance up to 250 miles.
                    </p>

                    <div className="distance-options-section">
                        <h4 className="distance-subheader">Edit distance</h4>
                        
                        <div className="distance-options-list">
                            {distanceOptions.map((option) => (
                                <label 
                                    key={option.value} 
                                    className="distance-option"
                                >
                                    <input
                                        type="radio"
                                        name="distance"
                                        value={option.value}
                                        checked={selectedDistance === option.value}
                                        onChange={() => handleDistanceChange(option.value)}
                                        className="distance-radio"
                                    />
                                    <span className="distance-option-label">{option.label}</span>
                                </label>
                            ))}

                            <label className="distance-option distance-custom-option">
                                <input
                                    type="radio"
                                    name="distance"
                                    value="custom"
                                    checked={selectedDistance === 'custom'}
                                    onChange={() => handleDistanceChange('custom')}
                                    className="distance-radio"
                                />
                                <span className="distance-option-label distance-custom-label">
                                    Within
                                    <input
                                        type="text"
                                        className={`distance-custom-input ${distanceError ? 'error' : ''}`}
                                        value={customDistance}
                                        onChange={handleCustomDistanceChange}
                                        // NOTE: deliberately no onFocus handler here (previously
                                        // this called setSelectedDistance('custom') on focus).
                                        // Because unchecked radio inputs are skipped in native Tab
                                        // order (only the checked radio in a `name` group is a tab
                                        // stop), tabbing away from a preset option like "Within 25
                                        // miles" landed directly on THIS input next - merely
                                        // receiving focus (not typing) was flipping selectedDistance
                                        // to 'custom' while customDistance was still empty, which
                                        // disables Apply (isApplyDisabled) and made handleApply()
                                        // no-op even though the user still had "25 miles" visibly
                                        // checked. Selecting "custom" now only happens when the user
                                        // actually types a value (handleCustomDistanceChange below)
                                        // or explicitly clicks/checks the custom radio itself
                                        // (handleDistanceChange('custom')) - not from focus alone.
                                        placeholder=""
                                        aria-label="Custom distance in miles"
                                        aria-invalid={distanceError ? 'true' : 'false'}
                                        aria-describedby={distanceError ? 'distance-error-msg' : undefined}
                                        maxLength="4"
                                    />
                                    miles
                                </span>
                            </label>

                            {/* Error Message */}
                            {distanceError && (
                                <div id="distance-error-msg" className="distance-error-message" role="alert">
                                    <img src={hazardIcon} alt="" className="error-icon" />
                                    <span>{distanceError}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="distance-panel-footer">
                    {/* aria-disabled (not the native `disabled` attribute) so keyboard users
                        can still Tab to and reach this button while it's logically disabled
                        (e.g. "custom" selected but no value typed yet) — a native `disabled`
                        button is removed from the tab order entirely, which was the reported
                        "Apply distance button is not accessible... user gets trapped" bug:
                        keyboard-only users had no way to ever focus it to discover why it
                        wasn't working, and Tab would skip straight past it out of the panel
                        (see the focus-trap fix above). handleApply() already no-ops when the
                        custom distance is empty/invalid, so it's safe to keep wired to
                        onClick even in this state. */}
                    <button 
                        className={`distance-apply-button${isApplyDisabled ? ' is-disabled' : ''}`}
                        onClick={handleApply}
                        aria-disabled={isApplyDisabled ? 'true' : 'false'}
                        type="button"
                    >
                        Apply distance
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default DistancePanel;
